import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import {
  fetchMyQuotes, fetchMyPhotos, uploadPhotoR2, deletePhoto,
  authUpdateProfile, getMyFavorites, addFavorite, removeFavorite, fetchProducts,
  resolveUploadUrl,
  type ApiQuote, type CustomerPhoto, type ApiUser, type FavoriteProduct, type ApiProduct,
} from '../lib/api';
import { printQuotePDF, OWNER_COMPANY } from '../lib/quotePdf';
import SEOHead from '../components/SEOHead';
import PasswordSettings from '../components/PasswordSettings';
import {
  User, FileText, Camera, Edit3, Save, X, Plus, Loader2,
  CheckCircle2, Clock, AlertCircle, Mail, Phone, MapPin, Building2,
  Trash2, Calculator, Download, Heart,
} from 'lucide-react';

type Tab = 'gegevens' | 'offertes' | 'fotos' | 'favorieten';

export default function CustomerDashboard() {
  const { user, setUser, authLoading } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<Tab>('gegevens');

  // Profile editing
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    company_name: '',
    btw_number: '',
    address: '',
    city: '',
    postal_code: '',
    notes: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Quotes
  const [quotes, setQuotes] = useState<ApiQuote[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(true);

  // Photos
  const [photos, setPhotos] = useState<CustomerPhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Init profile form from user
  useEffect(() => {
    if (user) {
      const u = user as ApiUser;
      setProfileForm({
        name: u.name || '',
        phone: (u as Record<string, unknown>).phone as string || '',
        company_name: (u as Record<string, unknown>).company_name as string || '',
        btw_number: (u as Record<string, unknown>).btw_number as string || '',
        address: (u as Record<string, unknown>).address as string || '',
        city: (u as Record<string, unknown>).city as string || '',
        postal_code: (u as Record<string, unknown>).postal_code as string || '',
        notes: (u as Record<string, unknown>).notes as string || '',
      });
    }
  }, [user]);

  // Fetch quotes
  useEffect(() => {
    if (!user) return;
    setQuotesLoading(true);
    fetchMyQuotes()
      .then(setQuotes)
      .catch(() => setQuotes([]))
      .finally(() => setQuotesLoading(false));
  }, [user]);

  // Fetch photos
  useEffect(() => {
    if (!user) return;
    setPhotosLoading(true);
    fetchMyPhotos(user.id)
      .then(setPhotos)
      .catch(() => setPhotos([]))
      .finally(() => setPhotosLoading(false));
  }, [user]);

  const handleSaveProfile = async () => {
    setProfileLoading(true);
    setProfileError('');
    try {
      const updated = await authUpdateProfile(profileForm);
      setUser(updated);
      setEditing(false);
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err: unknown) {
      setProfileError((err as { message?: string })?.message || t('customer.data.saveFailed'));
    } finally {
      setProfileLoading(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Upload limits — kept here as constants so they're easy to tune later.
  // Mirror these on the server (uploads.ts) for defense-in-depth.
  const MAX_PHOTOS = 5;
  const MAX_FILE_SIZE_MB = 5;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUploadPhotos = useCallback(async (files: FileList | null) => {
    if (!files || !user) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setUploadError(t('customer.photos.maxReached'));
      return;
    }

    const incoming = Array.from(files);
    // Pre-validate each file BEFORE base64-encoding (which is expensive).
    for (const f of incoming) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        setUploadError(`${f.name}: ${t('customer.photos.errType')}`);
        return;
      }
      if (f.size > MAX_FILE_SIZE_BYTES) {
        const mb = (f.size / 1024 / 1024).toFixed(1);
        setUploadError(`${f.name}: ${t('customer.photos.errSize')} (${mb} MB)`);
        return;
      }
    }

    const toUpload = incoming.slice(0, remaining);
    if (incoming.length > remaining) {
      setUploadError(t('customer.photos.errTrimmed').replace('{n}', String(remaining)));
    } else {
      setUploadError(null);
    }

    setUploadLoading(true);
    try {
      // v3.2.59 — direct multipart-upload naar R2 (vroeger: base64 in DB).
      // Foto's worden sequentieel geüpload zodat de quota-check op de server
      // tussen uploads accuraat is en geen race-condition geeft.
      for (const f of toUpload) {
        await uploadPhotoR2(f);
      }
      const updated = await fetchMyPhotos(user.id);
      setPhotos(updated);
    } catch (e) {
      setUploadError((e as Error).message ?? t('customer.photos.errGeneric'));
    } finally {
      setUploadLoading(false);
    }
  }, [photos.length, user, t]);

  const handleDeletePhoto = async (photoId: string) => {
    if (!user) return;
    try {
      await deletePhoto(photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch {
      // silent
    }
  };

  if (authLoading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-[#faf7f3]">
        <Loader2 size={24} className="animate-spin text-[#c47a3a]" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login?redirect=/dashboard" replace />;

  // Redirect admin/owner to /admin
  if (user.role === 'owner' || user.role === 'superadmin') {
    return <Navigate to="/admin" replace />;
  }

  // V6.25 — een handelaar (of zijn medewerker) die op /dashboard belandt, kwam
  // in het klantscherm terecht en zag zijn offertes en bestellingen niet. Hij
  // hoort in zijn eigen handelaarsportaal.
  if (user.role === 'reseller') {
    return <Navigate to="/reseller-dashboard" replace />;
  }

  const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
    draft: { label: t('customer.status.draft'), color: 'text-gray-600', bg: 'bg-gray-100' },
    sent: { label: t('customer.status.sent'), color: 'text-blue-600', bg: 'bg-blue-50' },
    accepted: { label: t('customer.status.accepted'), color: 'text-green-600', bg: 'bg-green-50' },
    rejected: { label: t('customer.status.rejected'), color: 'text-red-600', bg: 'bg-red-50' },
    expired: { label: t('customer.status.expired'), color: 'text-yellow-600', bg: 'bg-yellow-50' },
  };

  const tabs: { key: Tab; label: string; icon: typeof User }[] = [
    { key: 'gegevens',   label: t('customer.tab.myData'),      icon: User },
    { key: 'offertes',   label: t('customer.tab.myQuotes'),    icon: FileText },
    { key: 'favorieten', label: t('customer.tab.myFavorites'), icon: Heart },
    { key: 'fotos',      label: t('customer.tab.myPhotos'),    icon: Camera },
  ];

  return (
    <div className="pt-16 md:pt-[72px]">
      <SEOHead page="dashboard" />

      {/* Header */}
      <section className="bg-[#1a1a1a] text-white py-6 px-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <div className="w-14 h-14 bg-[#c47a3a] rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold truncate">{user.name}</h1>
              {(user as Record<string, unknown>).btw_number ? (
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-[10px] font-bold rounded-full uppercase tracking-wide">{t('customer.business')}</span>
              ) : (
                <span className="px-2 py-0.5 bg-white/10 text-gray-400 text-[10px] font-bold rounded-full uppercase tracking-wide">{t('customer.private')}</span>
              )}
            </div>
            <p className="text-sm text-gray-400">{user.email}</p>
            {(user as Record<string, unknown>).btw_number && (
              <p className="text-xs text-blue-300 mt-0.5">{t('customer.btwAccount')} — {(user as Record<string, unknown>).btw_number as string}</p>
            )}
          </div>
        </div>
      </section>

      {/* Tab bar */}
      <section className="bg-white border-b border-gray-100 sticky top-16 md:top-[72px] z-30">
        <div className="max-w-5xl mx-auto px-4 flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-[#c47a3a] text-[#c47a3a]'
                  : 'border-transparent text-[#8a7e74] hover:text-[#1a1a1a]'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#faf7f3] min-h-[60vh] py-6 px-4">
        <div className="max-w-5xl mx-auto">

          {/* ─── Mijn Gegevens ───────────────────────────────────────────── */}
          {activeTab === 'gegevens' && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold">{t('customer.data.title')}</h2>
                {!editing ? (
                  <button
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#c47a3a] border border-[#c47a3a]/30 rounded-lg hover:bg-[#c47a3a]/5 transition-colors"
                  >
                    <Edit3 size={14} /> {t('customer.data.edit')}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditing(false)}
                      className="px-4 py-2 text-sm text-[#8a7e74] hover:text-[#1a1a1a] transition-colors"
                    >
                      {t('customer.data.cancel')}
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      disabled={profileLoading}
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#c47a3a] text-white text-sm font-medium rounded-lg hover:bg-[#b06a2a] transition-colors disabled:opacity-50"
                    >
                      {profileLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      {t('customer.data.save')}
                    </button>
                  </div>
                )}
              </div>

              {profileSuccess && (
                <div className="mx-6 mt-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3">
                  <CheckCircle2 size={16} /> {t('customer.data.saved')}
                </div>
              )}
              {profileError && (
                <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                  <AlertCircle size={16} /> {profileError}
                </div>
              )}

              <div className="p-6 space-y-5">
                {editing ? (
                  <>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#8a7e74] mb-1">{t('customer.data.name')}</label>
                        <input type="text" value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#8a7e74] mb-1">{t('customer.data.phone')}</label>
                        <input type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8a7e74] mb-1">{t('customer.data.address')}</label>
                      <input type="text" value={profileForm.address} onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#8a7e74] mb-1">{t('customer.data.postalCode')}</label>
                        <input type="text" value={profileForm.postal_code} onChange={e => setProfileForm(p => ({ ...p, postal_code: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#8a7e74] mb-1">{t('customer.data.city')}</label>
                        <input type="text" value={profileForm.city} onChange={e => setProfileForm(p => ({ ...p, city: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#8a7e74] mb-1">{t('customer.data.company')}</label>
                        <input type="text" value={profileForm.company_name} onChange={e => setProfileForm(p => ({ ...p, company_name: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#8a7e74] mb-1">{t('customer.data.btw')}</label>
                        <input type="text" value={profileForm.btw_number} onChange={e => setProfileForm(p => ({ ...p, btw_number: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8a7e74] mb-1">{t('customer.data.notes')}</label>
                      <textarea value={profileForm.notes} onChange={e => setProfileForm(p => ({ ...p, notes: e.target.value.slice(0, 500) }))}
                        rows={3} className="w-full px-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] resize-none" />
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <InfoRow icon={User} label={t('customer.data.name')} value={user.name} />
                    <InfoRow icon={Mail} label={t('customer.data.email')} value={user.email} />
                    <InfoRow icon={Phone} label={t('customer.data.phone')} value={profileForm.phone || '—'} />
                    <InfoRow icon={MapPin} label={t('customer.data.address')} value={
                      [profileForm.address, profileForm.postal_code, profileForm.city].filter(Boolean).join(', ') || '—'
                    } />
                    <InfoRow icon={Building2} label={t('customer.data.companyLabel')} value={
                      [profileForm.company_name, profileForm.btw_number].filter(Boolean).join(' — ') || '—'
                    } />
                    {profileForm.notes && (
                      <InfoRow icon={FileText} label={t('customer.data.notes')} value={profileForm.notes} />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Mijn Offertes ──────────────────────────────────────────── */}
          {activeTab === 'offertes' && (
            <div>
              {/* v5.29: één ingang per situatie — knop rechtsboven alleen als
                   er al offertes zijn; anders staat de knop in het midden. */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">{t('customer.quotes.title')}</h2>
                {quotes.length > 0 && (
                  <Link
                    to="/#calculator"
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#c47a3a] text-white text-sm font-medium rounded-lg hover:bg-[#b06a2a] transition-colors"
                  >
                    <Calculator size={14} /> + Nieuwe offerte maken
                  </Link>
                )}
              </div>

              {quotesLoading ? (
                <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                  <Loader2 size={24} className="animate-spin text-[#c47a3a] mx-auto" />
                </div>
              ) : quotes.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                  <div className="w-14 h-14 bg-[#c47a3a]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FileText size={28} className="text-[#c47a3a]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{t('customer.quotes.noQuotes')}</h3>
                  <p className="text-sm text-[#8a7e74] mb-4">{t('customer.quotes.noQuotesDesc')}</p>
                  <Link to="/#calculator" className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#c47a3a] text-white text-sm font-semibold rounded-lg hover:bg-[#b06a2a] transition-colors">
                    <Calculator size={14} /> + Nieuwe offerte maken
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {quotes.map(q => {
                    const s = statusConfig[q.status] || statusConfig.draft;
                    const d = new Date(q.created_at * 1000);
                    const date = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                    const isB2B = !!(q.is_b2b && q.customer_btw_number);
                    return (
                      <div key={q.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.color}`}>
                                {s.label}
                              </span>
                              <span className="text-xs text-[#8a7e74]">{date}</span>
                            </div>
                            {q.notes_nl && (
                              <p className="text-sm text-[#4a4a4a] truncate max-w-md mt-1">{q.notes_nl}</p>
                            )}
                            {isB2B && (
                              <div className="mt-2 flex items-center gap-2 text-[11px] text-blue-600">
                                <Building2 size={12} />
                                <span>
                                  {(user as Record<string, unknown>).company_name && <>{(user as Record<string, unknown>).company_name as string} — </>}
                                  {q.customer_btw_number}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="text-right flex-shrink-0">
                              {isB2B ? (
                                <>
                                  <p className="text-sm text-[#8a7e74]">
                                    {t('customer.quotes.subtotal')} €{q.total_excl_vat.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}
                                  </p>
                                  <p className="text-sm text-[#8a7e74]">
                                    BTW {Math.round((q.vat_rate || 0.21) * 100)}%: €{(q.total_incl_vat - q.total_excl_vat).toLocaleString('nl-BE', { minimumFractionDigits: 2 })}
                                  </p>
                                  <p className="text-lg font-bold text-[#c47a3a]">
                                    {t('customer.quotes.totalInclVat')} €{q.total_incl_vat.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-lg font-bold text-[#c47a3a]">
                                    €{q.total_incl_vat.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}
                                  </p>
                                  <p className="text-[11px] text-[#8a7e74]">{t('customer.quotes.priceInclVat')}</p>
                                </>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                // v3.2.125 — window.open() MOET synchroon in de
                                // click-handler context gebeuren, anders blokkeert
                                // de pop-up blocker. Open eerst het venster, dan
                                // pas printQuotePDF aanroepen.
                                const targetWindow = window.open('', '_blank');
                                if (!targetWindow) {
                                  alert('Pop-up blocker actief. Sta pop-ups toe voor deze site en probeer opnieuw.');
                                  return;
                                }
                                targetWindow.document.write('<html><body style="font-family:system-ui;padding:40px;color:#8a4423">Offerte aan het laden…</body></html>');
                                printQuotePDF({
                                  quote: {
                                    id: q.id,
                                    customer_name: q.customer_name,
                                    customer_email: q.customer_email,
                                    customer_phone: q.customer_phone,
                                    customer_btw_number: q.customer_btw_number,
                                    status: q.status,
                                    total_excl_vat: q.total_excl_vat,
                                    total_incl_vat: q.total_incl_vat,
                                    is_b2b: q.is_b2b,
                                    notes_nl: q.notes_nl,
                                    created_at: q.created_at,
                                    // expires_at: not in ApiQuote, but the renderer
                                    // safely omits the row when undefined.
                                  },
                                  company: OWNER_COMPANY,
                                  isResellerRouted: false,  // customer always sees owner brand
                                  t,
                                  targetWindow,
                                }).catch((e: Error) => {
                                  try { targetWindow.close(); } catch { /* ignore */ }
                                  alert('PDF maken mislukte: ' + e.message);
                                });
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#c47a3a] border border-[#c47a3a]/30 rounded-lg hover:bg-[#c47a3a]/5 transition-colors mt-0.5"
                              title={t('customer.quotes.downloadPdf') || 'Download PDF'}
                            >
                              <Download size={12} /> PDF
                            </button>
                          </div>
                        </div>
                        {/* ─── Reseller-blok + plaatsing ───────────────────── */}
                        <QuoteResellerBlock quote={q} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ─── Mijn Favorieten ─────────────────────────────────────────── */}
          {activeTab === 'favorieten' && <FavoritesTab />}

          {/* ─── Mijn Foto's ────────────────────────────────────────────── */}
          {activeTab === 'fotos' && (
            <div>
              <div className="flex items-center justify-between mb-2 gap-3">
                <div>
                  <h2 className="text-lg font-bold">{t('customer.photos.title')}</h2>
                  <p className="text-xs text-[#8a7e74] mt-0.5">{photos.length}/{MAX_PHOTOS} {t('customer.photos.count')}</p>
                </div>
                {photos.length < MAX_PHOTOS && (
                  <button
                    onClick={() => { setUploadError(null); fileInputRef.current?.click(); }}
                    disabled={uploadLoading}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#c47a3a] text-white text-sm font-medium rounded-lg hover:bg-[#b06a2a] transition-colors disabled:opacity-50 min-h-[44px]"
                  >
                    {uploadLoading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    {t('customer.photos.addPhoto')}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={e => handleUploadPhotos(e.target.files)}
                />
              </div>

              {/* Upload instructions */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-900">
                <p className="font-semibold mb-1">{t('customer.photos.rulesTitle')}</p>
                <ul className="list-disc list-inside space-y-0.5 text-blue-800">
                  <li>{t('customer.photos.ruleMax').replace('{n}', String(MAX_PHOTOS))}</li>
                  <li>{t('customer.photos.ruleSize').replace('{mb}', String(MAX_FILE_SIZE_MB))}</li>
                  <li>{t('customer.photos.ruleType')}</li>
                  <li>{t('customer.photos.ruleResolution')}</li>
                </ul>
              </div>

              {uploadError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{uploadError}</span>
                </div>
              )}

              {photosLoading ? (
                <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                  <Loader2 size={24} className="animate-spin text-[#c47a3a] mx-auto" />
                </div>
              ) : photos.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                  <div className="w-14 h-14 bg-[#c47a3a]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Camera size={28} className="text-[#c47a3a]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{t('customer.photos.noPhotos')}</h3>
                  <p className="text-sm text-[#8a7e74] mb-4">{t('customer.photos.noPhotosDesc')}</p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#c47a3a] text-white text-sm font-semibold rounded-lg hover:bg-[#b06a2a] transition-colors"
                  >
                    <Plus size={14} /> {t('customer.photos.upload')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {photos.map(photo => (
                    <div key={photo.id} className="relative group bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                      <img
                        src={photo.photo_url ? resolveUploadUrl(photo.photo_url) : photo.photo_data}
                        alt={photo.caption || t('customer.photos.projectPhoto')}
                        className="w-full h-40 object-cover"
                      />
                      <button
                        onClick={() => handleDeletePhoto(photo.id)}
                        className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 size={12} className="text-white" />
                      </button>
                      {photo.caption && (
                        <div className="px-3 py-2">
                          <p className="text-xs text-[#4a4a4a]">{photo.caption}</p>
                        </div>
                      )}
                      <div className="px-3 pb-2">
                        <p className="text-[10px] text-[#8a7e74]">
                          {new Date(photo.created_at * 1000).toLocaleDateString('nl-BE')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* v5 blok 8: eigen bestellingen & facturen */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-6">
        <MyOrdersInvoices />
      </section>

      {/* v5 blok 7: paswoord instellen — klassieke login naast de mailcode */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-6">
        <PasswordSettings />
      </section>
    </div>
  );
}

/** v5 blok 8: bestellingen en facturen van de ingelogde koper. */
function MyOrdersInvoices() {
  const [orders, setOrders] = useState<import('../lib/api').AdminOrder[]>([]);
  const [invoices, setInvoices] = useState<import('../lib/api').AdminInvoice[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    void (async () => {
      try {
        const api = await import('../lib/api');
        const [o, i] = await Promise.all([api.fetchMyOrders(), api.fetchMyInvoices()]);
        setOrders(o); setInvoices(i);
      } catch { /* leeg laten */ } finally { setLoaded(true); }
    })();
  }, []);
  const dl = async (inv: import('../lib/api').AdminInvoice) => {
    const win = window.open('', '_blank');
    const { printInvoicePDF } = await import('../lib/invoicePdf');
    printInvoicePDF(inv, { targetWindow: win, orderNumber: inv.order_number ?? null });
  };
  if (!loaded || (orders.length === 0 && invoices.length === 0)) return null;
  const statusNl: Record<string, string> = { nieuw: 'Nieuw', bevestigd: 'Bevestigd', geleverd: 'Geleverd', geannuleerd: 'Geannuleerd' };
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-5">
      <h3 className="text-sm font-bold text-[#1a1a1a] mb-3">Mijn bestellingen & facturen</h3>
      {orders.map(o => (
        <div key={o.id} className="flex items-center justify-between py-2 border-b border-gray-50 text-sm">
          <span>Bestelling <span className="font-mono text-xs">#{o.order_number}</span> — {statusNl[o.status] ?? o.status}
            {o.payment_terms === 'vooraf' && o.payment_status !== 'betaald' && (
              <span className="ml-2 text-xs text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded font-semibold">Wacht op betaling vóór levering</span>
            )}
          </span>
          <span className="font-semibold">€{o.total_incl_vat.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}</span>
        </div>
      ))}
      {invoices.map(inv => (
        <div key={inv.id} className="flex items-center justify-between py-2 border-b border-gray-50 text-sm">
          <span>Factuur <span className="font-mono text-xs">FAC-{new Date(inv.created_at * 1000).getFullYear()}-{String(inv.invoice_number).padStart(4, '0')}</span>
            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded font-semibold ${inv.status === 'betaald' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}>{inv.status}</span>
          </span>
          <button onClick={() => void dl(inv)} className="px-2.5 py-1 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded text-xs font-bold">PDF</button>
        </div>
      ))}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: typeof User; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon size={16} className="text-[#c47a3a] mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74]">{label}</p>
        <p className="text-sm text-[#1a1a1a]">{value}</p>
      </div>
    </div>
  );
}

function FavoritesTab() {
  const { t } = useLanguage();
  const [favorites, setFavorites] = useState<FavoriteProduct[]>([]);
  const [allProducts, setAllProducts] = useState<ApiProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const favoriteIds = useMemo(() => new Set(favorites.map(f => f.product_id)), [favorites]);

  // Load both: my favorites AND the full product catalog. Both are needed
  // because we want to show the catalog inline so the user can heart things
  // directly from this tab — no detour to the homepage required.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [favs, prods] = await Promise.all([
        getMyFavorites(),
        fetchProducts(),
      ]);
      setFavorites(favs);
      // Filter out remnant/sold items from the catalog list — Filip's
      // remnant page handles those separately.
      setAllProducts(prods.filter(p => !((p as ApiProduct & { is_remnant?: number; is_sold?: number }).is_remnant || (p as ApiProduct & { is_remnant?: number; is_sold?: number }).is_sold)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const toggleFavorite = async (productId: string) => {
    const isFav = favoriteIds.has(productId);
    // Optimistic UI
    if (isFav) {
      setFavorites(prev => prev.filter(i => i.product_id !== productId));
    } else {
      const product = allProducts.find(p => p.id === productId);
      if (product) {
        setFavorites(prev => [...prev, {
          product_id: product.id,
          name: product.name,
          slug: product.slug,
          color: product.color,
          color_hex: product.color_hex,
          category: product.category,
          dimensions: product.dimensions,
          primary_image: product.primary_image,
          locked_price: (product as ApiProduct & { locked_price?: number | null }).locked_price ?? null,
          note: null,
          created_at: Math.floor(Date.now() / 1000),
        } as FavoriteProduct]);
      }
    }
    try {
      if (isFav) await removeFavorite(productId);
      else await addFavorite(productId);
    } catch {
      // Rollback on error
      void load();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Heart size={18} className="text-red-500" fill="currentColor" />
            {t('customer.favorites.title')}
          </h2>
          <p className="text-xs text-[#8a7e74] mt-0.5">{t('customer.favorites.desc')}</p>
        </div>
        {favorites.length > 0 && (
          <span className="px-2 py-0.5 bg-red-50 text-red-700 text-xs font-bold rounded-full">
            {favorites.length} {favorites.length === 1 ? 'favoriet' : 'favorieten'}
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-[#8a7e74] py-6 text-center">{t('customer.favorites.loading')}</p>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      ) : (
        <>
          {/* ─── Geselecteerde favorieten (eerst) ──────────────────────────── */}
          {favorites.length > 0 && (
            <section className="mb-8">
              <h3 className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Heart size={12} className="text-red-500" fill="currentColor" /> Uw favorieten
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {favorites.map(p => (
                  <article key={p.product_id} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                    <div className="aspect-[4/3] bg-gray-100 relative">
                      {p.primary_image ? (
                        <img src={p.primary_image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: p.color_hex }} />
                      )}
                      <button
                        onClick={() => void toggleFavorite(p.product_id)}
                        className="absolute top-2 right-2 w-9 h-9 bg-white/95 hover:bg-white text-red-500 rounded-full flex items-center justify-center shadow-md"
                        aria-label={t('customer.favorites.remove')}
                        title={t('customer.favorites.remove')}
                      >
                        <Heart size={16} fill="currentColor" />
                      </button>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-bold text-[#1a1a1a]" style={{ fontFamily: 'Playfair Display' }}>{p.name}</h3>
                      <p className="text-[11px] text-[#8a7e74] capitalize mb-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full mr-1 align-middle" style={{ backgroundColor: p.color_hex }} />
                        {p.color} · {p.category}
                      </p>
                      {p.locked_price != null && (
                        <p className="text-sm font-bold text-[#c47a3a]">€{p.locked_price.toFixed(2)}/m²</p>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* ─── Volledige catalogus — klik hartje om te (de)markeren ──────── */}
          <section>
            <div className="mb-3">
              <h3 className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide flex items-center gap-1.5">
                <Heart size={12} className="text-gray-400" /> Volledige catalogus
              </h3>
              <p className="text-[11px] text-[#8a7e74] mt-1">
                Klik op het hartje om een steensoort aan uw favorieten toe te voegen of te verwijderen.
                Filip en de verdeler in uw regio kunnen deze voorkeuren zien wanneer ze een offerte voor u opstellen.
              </p>
            </div>
            {allProducts.length === 0 ? (
              <p className="text-sm text-[#8a7e74] py-6 text-center bg-[#faf7f3] rounded-xl">
                Catalogus laden mislukt. Probeer later opnieuw.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {allProducts.map(p => {
                  const isFav = favoriteIds.has(p.id);
                  return (
                    <article
                      key={p.id}
                      className={`group bg-white border-2 rounded-xl overflow-hidden shadow-sm transition-all ${
                        isFav ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <div className="aspect-[4/3] bg-gray-100 relative">
                        {p.primary_image ? (
                          <img src={p.primary_image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full" style={{ background: p.color_hex }} />
                        )}
                        <button
                          onClick={() => void toggleFavorite(p.id)}
                          className={`absolute top-1.5 right-1.5 w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-all ${
                            isFav
                              ? 'bg-red-500 text-white scale-100'
                              : 'bg-white/90 text-gray-400 hover:bg-white hover:text-red-500 hover:scale-110'
                          }`}
                          aria-label={isFav ? t('customer.favorites.remove') : 'Toevoegen aan favorieten'}
                          title={isFav ? 'Uit favorieten verwijderen' : 'Toevoegen aan favorieten'}
                        >
                          <Heart size={15} fill={isFav ? 'currentColor' : 'none'} strokeWidth={2.2} />
                        </button>
                      </div>
                      <div className="p-2.5">
                        <h4 className="text-xs font-bold text-[#1a1a1a] truncate" style={{ fontFamily: 'Playfair Display' }}>
                          {p.name}
                        </h4>
                        <p className="text-[10px] text-[#8a7e74] capitalize mt-0.5 flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: p.color_hex }} />
                          {p.color} · {p.category}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// QuoteResellerBlock — toont op een offerte:
//   • Welke verdeler (reseller) deze offerte behandelt (of "Direct" = Filip zelf)
//   • Wat de plaatsings-keuze van de klant was (zelf/vriend/installateur)
//   • Bij keuze "installateur": een lijst van installateurs die de reseller
//     heeft geregistreerd (uit reseller.installers_json), zodat de klant ziet
//     wie er mogelijk komt plaatsen.
//
// Wordt enkel onder de offerte-card gerenderd; niet als losse sectie.
// ═══════════════════════════════════════════════════════════════════════════════

interface ResellerInstallerRow {
  company_name: string;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
}

function QuoteResellerBlock({ quote }: { quote: ApiQuote }) {
  // Geen reseller én geen installer-keuze → niets te tonen
  const hasReseller = !!quote.reseller_company_name;
  const wantsInstaller = quote.installer_choice === 'installer';
  if (!hasReseller && !quote.installer_choice) return null;

  // Parse installers veilig
  const installers: ResellerInstallerRow[] = (() => {
    if (!quote.reseller_installers_json) return [];
    try {
      const parsed = JSON.parse(quote.reseller_installers_json);
      return Array.isArray(parsed) ? parsed as ResellerInstallerRow[] : [];
    } catch { return []; }
  })();

  const installerLabel = quote.installer_choice === 'installer' ? 'U wenst een installateur'
                       : quote.installer_choice === 'self'      ? 'U plaatst zelf'
                       : quote.installer_choice === 'friend'    ? 'Een kennis plaatst voor u'
                       : null;

  return (
    <div className="border-t border-gray-100 bg-gradient-to-r from-amber-50/40 to-emerald-50/40 px-4 py-3 space-y-2">
      {/* Reseller info */}
      {hasReseller ? (
        <div className="flex items-start gap-3">
          {quote.reseller_logo_url ? (
            <img src={quote.reseller_logo_url} alt={quote.reseller_company_name ?? ''} className="w-10 h-10 rounded object-contain bg-white border border-amber-200 p-0.5" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center text-amber-800 font-bold flex-shrink-0">
              {quote.reseller_company_name?.charAt(0) ?? 'R'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold text-amber-900 uppercase tracking-wide">Uw verdeler in de regio</p>
            <p className="text-sm font-bold text-[#1a1a1a]">{quote.reseller_company_name}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[#4a4a4a]">
              {quote.reseller_contact_phone && <span>📞 {quote.reseller_contact_phone}</span>}
              {quote.reseller_contact_email && <span>✉ {quote.reseller_contact_email}</span>}
              {quote.reseller_website && (
                <a href={quote.reseller_website} target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">
                  🌐 Website
                </a>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-[11px] text-[#8a7e74]">
          📍 Deze offerte wordt rechtstreeks door <strong>Het Gevelsteenbedrijf</strong> behandeld.
        </div>
      )}

      {/* Plaatsing-keuze badge */}
      {installerLabel && (
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold ${
          wantsInstaller ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                         : 'bg-gray-100 text-gray-700 border border-gray-200'
        }`}>
          🔨 {installerLabel}
        </div>
      )}

      {/* Lijst van installateurs als de klant er een wilt */}
      {wantsInstaller && installers.length > 0 && (
        <div className="bg-white border border-emerald-200 rounded-lg p-2.5 mt-1">
          <p className="text-[10px] font-bold text-emerald-900 uppercase tracking-wide mb-1.5">
            Mogelijke installateurs in uw regio ({installers.length})
          </p>
          <div className="space-y-1">
            {installers.slice(0, 5).map((inst, idx) => (
              <div key={idx} className="flex items-baseline gap-2 text-[11px]">
                <span className="font-semibold text-[#1a1a1a]">{inst.company_name}</span>
                {inst.city && <span className="text-[#8a7e74]">· {inst.city}</span>}
                {inst.phone && <span className="text-[#4a4a4a]">📞 {inst.phone}</span>}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#8a7e74] mt-1.5 italic">
            Uw verdeler neemt contact op om de definitieve installateur af te spreken.
          </p>
        </div>
      )}

      {/* Geen installateurs gekoppeld aan reseller */}
      {wantsInstaller && hasReseller && installers.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-800">
          ⏳ Uw verdeler regelt de installateur — u wordt zo snel mogelijk gecontacteerd.
        </div>
      )}
    </div>
  );
}
